/**
 * 背景ツアー「福岡めぐり」の豆知識カード。
 *
 * ホットスポットマーカーをタップすると開き、
 * 名所の豆知識・関連料理・地図検索リンクを表示する。
 */

import type { GameContext } from '../game/GameContext';
import type { TourHotspot } from '../game/config/types';
import { googleMapsSearchUrl } from '../game/utils/tour';

export interface TipsOpenPayload {
  hotspot: TourHotspot;
  resumeOnClose: boolean;
  /** true の場合、開いた時点で発見済みにする。図鑑からの再表示では false。 */
  markAsDiscovered?: boolean;
}

export class TipsCardModal {
  private readonly context: GameContext;
  private readonly root: HTMLElement | null;
  private readonly titleEl: HTMLElement | null;
  private readonly areaEl: HTMLElement | null;
  private readonly bodyEl: HTMLElement | null;
  private readonly relatedEl: HTMLElement | null;
  private readonly relatedIcon: HTMLImageElement | null;
  private readonly relatedName: HTMLElement | null;
  private readonly mapLink: HTMLAnchorElement | null;
  private readonly sourceEl: HTMLElement | null;
  private readonly closeBtn: HTMLButtonElement | null;
  private currentHotspot: TourHotspot | null = null;
  private resumeOnClose = false;

  constructor(context: GameContext, root: ParentNode = document) {
    this.context = context;
    this.root = root.querySelector<HTMLElement>('[data-ui="tips-modal"]');
    this.titleEl = root.querySelector<HTMLElement>('[data-ui="tips-title"]');
    this.areaEl = root.querySelector<HTMLElement>('[data-ui="tips-area"]');
    this.bodyEl = root.querySelector<HTMLElement>('[data-ui="tips-body"]');
    this.relatedEl = root.querySelector<HTMLElement>('[data-ui="tips-related"]');
    this.relatedIcon = root.querySelector<HTMLImageElement>('[data-ui="tips-related-icon"]');
    this.relatedName = root.querySelector<HTMLElement>('[data-ui="tips-related-name"]');
    this.mapLink = root.querySelector<HTMLAnchorElement>('[data-ui="tips-map"]');
    this.sourceEl = root.querySelector<HTMLElement>('[data-ui="tips-source"]');
    this.closeBtn = root.querySelector<HTMLButtonElement>('[data-ui="tips-close"]');

    this.closeBtn?.addEventListener('click', () => this.hide());
    this.root
      ?.querySelector<HTMLElement>('[data-ui="tips-backdrop"]')
      ?.addEventListener('click', () => this.hide());

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.isOpen()) this.hide();
    });
  }

  /** モーダルが閉じられたときに呼ばれるコールバック。 */
  onClose?: (resumeOnClose: boolean) => void;

  open(payload: TipsOpenPayload): void {
    const { hotspot, resumeOnClose, markAsDiscovered = true } = payload;
    if (!this.root) {
      console.warn('[TipsCardModal] root 要素が見つかりません');
      return;
    }

    this.currentHotspot = hotspot;
    this.resumeOnClose = resumeOnClose;

    // 発見済みにする（図鑑からの再表示では発見扱いにしない）
    if (markAsDiscovered) {
      const isNew = this.context.storage.discoverTip(hotspot.id);
      if (isNew) {
        this.context.bus.emit('tour:discover', { tipId: hotspot.id });
      }
    }

    if (this.titleEl) this.titleEl.textContent = hotspot.title;
    if (this.areaEl) {
      const tour = this.context.stage.scenery?.tour;
      const scene = tour
        ? tour.scenes.find((scene) => scene.hotspots.some((h) => h.id === hotspot.id))
        : undefined;
      this.areaEl.textContent = scene ? `${scene.name} / ${scene.area}` : '';
    }
    if (this.bodyEl) this.bodyEl.textContent = hotspot.body;
    this.renderSource(hotspot.source);

    this.renderRelated(hotspot.relatedItemId);

    if (this.mapLink) {
      this.mapLink.href = googleMapsSearchUrl(hotspot.mapQuery);
      this.mapLink.textContent = `${hotspot.mapQuery} を地図で見る`;
      this.mapLink.setAttribute('aria-label', `${hotspot.mapQuery} を Google マップで開く`);
    }

    this.root.dataset.visible = 'true';
    this.closeBtn?.focus();
  }

  /**
   * 出典。公的機関の資料に拠っていることが、このゲームのいちばんの信用になる。
   * データにはあったのに画面に出していなかった。
   */
  private renderSource(source: string | undefined): void {
    if (!this.sourceEl) return;
    this.sourceEl.replaceChildren();
    if (!source) {
      this.sourceEl.hidden = true;
      return;
    }
    let host = source;
    try {
      host = new URL(source).hostname.replace(/^www\./, '');
    } catch {
      // URL でなければそのまま出す
    }
    const link = document.createElement('a');
    link.href = source;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.textContent = host;
    this.sourceEl.append('出典：', link);
    this.sourceEl.hidden = false;
  }

  private renderRelated(itemId: string | undefined): void {
    if (!this.relatedEl) return;

    if (!itemId) {
      this.relatedEl.style.display = 'none';
      return;
    }

    const item = this.context.items.find((i) => i.id === itemId);
    if (!item) {
      this.relatedEl.style.display = 'none';
      return;
    }

    this.relatedEl.style.display = '';
    // まだ出会っていない料理は、豆知識からも名前と絵を明かさない
    if (!this.context.storage.isRevealed(item.id)) {
      if (this.relatedIcon) {
        this.relatedIcon.removeAttribute('src');
        this.relatedIcon.alt = '';
        this.relatedIcon.hidden = true;
      }
      if (this.relatedName) this.relatedName.textContent = '？？？（まだ出会っていない料理）';
      return;
    }
    if (this.relatedIcon) {
      this.relatedIcon.hidden = false;
      this.relatedIcon.src = this.context.icons.iconUrl(item, 64);
      this.relatedIcon.alt = item.name;
    }
    if (this.relatedName) this.relatedName.textContent = item.name;
  }

  hide(): void {
    if (!this.root) return;
    this.root.dataset.visible = 'false';
    const shouldResume = this.resumeOnClose;
    this.currentHotspot = null;
    this.resumeOnClose = false;
    this.onClose?.(shouldResume);
  }

  isOpen(): boolean {
    return this.root?.dataset.visible === 'true';
  }

  get current(): TourHotspot | null {
    return this.currentHotspot;
  }
}
